<template>
<!-- Station marker -->
<!-- <span v-for="station in stationsWithLocation()" :key="station.id"> -->
<span v-for="station in stationsStore.stationsArray" :key="station.id">
	<svg
		xmlns="http://www.w3.org/2000/svg"
		:width="marckWidth"
		:height="marckHeight"
		class="feather align-text-bottom station-marker"
		viewBox="0 0 16 16"
		:ref="station.id"
		@contextmenu.stop="(event) => showStationMarkerContextMenu(event, station)"
		@click="onClickStationMarker(station)"
	>
		<path :d="stationSvgPath" transform="translate(4,9) scale(0.5)"/>
		<path
			d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"
			transform="translate(0,-8)"
			:fill="stationMarkersColor[station.id]"
			stroke="black"
			stroke-width="0.3"
			stroke-linecap="round"
		/>
		<text
			x="100%"
			y="45"
			fill="black"
			transform="scale(0.5)"
			text-anchor="middle"
		>
			{{ station.eOut?.toFixed(2) }}
		</text>
	</svg>
</span>

<station-context-menu
	:position="stationContextMenuPosition"
	:visible="stationContextMenuVisible"
	:station="stationForContextMenu"
	:topShift="mapContainerEl.getBoundingClientRect().x"
	:leftShift="mapContainerEl.getBoundingClientRect().y"
	@removeLocation="removeStationLocation"
/>
</template>


<script>
import { Station } from '@/models/station.js'
import {
	Marker,
	Popup,
} from '@maptiler/sdk'
import { useStationsStore } from '@/stores/StationsStore.js'
import StationContextMenu from "@/components/MapView/StationContextMenu.vue"

export default {
	STATIONS_SOURCE_ID: 'stations',

	inject: [ 'stationSvgPath', ],

	components: {
		StationContextMenu,
	},

	props: {
		// Map object
		map: {
			type: Object,
			required: true,
		},
		mapContainerEl: {
			type: Object,
			required: true,
		},
		// Stations for show on map
		stations: {
			type: Array,
			required: true,
			default: new Array(),
		},
	},

	setup() {
		const stationsStore = useStationsStore()
	
		return {
			stationsStore,
		}
	},

	data() {
		return {
			mapMarkers: {}, // Object {station_id: Marker}
			mapMarkersOnScreen: {},
			stationMarkersColor: {}, // Object {station_id: color}
			stationsGeoJson: {
				type: "FeatureCollection",
				crs: {
					type: "name",
					properties: {
							name: "urn:ogc:def:crs:OGC:1.3:CRS84"
					}
				},
				features: [],
			},

			// Station context menu
			stationContextMenuVisible: false,
			stationContextMenuPosition: { top: 0, left: 0 },
			stationForContextMenu: undefined,
		}
	},

	async created() {
		//console.log("Created stations layer ", this.stations)
		this.updateStationMarksColor()
		this.updateStationsGeoJson()

		// On change page
		this.$watch(
			() => this.stations,
			(/*newVal, oldVal*/) => {
				//console.log("Update stations list. New one is ", newVal)
				this.updateStationMarksColor()
				this.updateStationsGeoJson()
			},
			{deep: true}
		)
	},

	async mounted() {
		// Map events handlers
		//
		// Fired immediately after all necessary resources have been downloaded
		// and the first visually complete rendering of the map has occurred
		this.map.on('load', () => {
			//console.log("Stations layer loaded.")

			this.addStationMarkersLayer()
		});

		// The `click` event
		this.map.on('click', function() {
			//console.log("Click event occured")
			this.hideStationContextMenu()
		}.bind(this));

		// Right button click
		this.map.on('contextmenu', function() {
			this.hideStationContextMenu()
		}.bind(this));

		// Drag map
		this.map.on('drag', function() {
			this.hideStationContextMenu()
		}.bind(this));

		// Zoom map
		this.map.on('zoomstart', function() {
			this.hideStationContextMenu()
		}.bind(this));

		//
		this.map.on('zoomend', function() {
			//console.log('A zoomend event occurred.');
			this.renderStationMarkers()
		}.bind(this));

		// after the GeoJSON data is loaded,
		// update markers on the screen and do so on every map move/moveend
		this.map.on('data', function (e) {
			if (e.sourceId !== this.$options.STATIONS_SOURCE_ID || !e.isSourceLoaded)
				return;
			//console.log(`A data update for "${this.$options.STATIONS_SOURCE_ID}" event occurred.`);
			this.renderStationMarkers()
		}.bind(this));
	},

	methods: {
		addStationMarkersLayer() {
			//console.log("Stations geojson", this.stationsGeoJson)
			// Push stations data to map
			this.map.addSource(this.$options.STATIONS_SOURCE_ID, {
				'type': 'geojson',
				'data': this.stationsGeoJson,
				//'data': 'https://docs.maptiler.com/sdk-js/assets/earthquakes.geojson',
				cluster: true,
				clusterMaxZoom: 14, // Max zoom to cluster points on
				clusterRadius: 20 // Radius of each cluster when clustering points (defaults to 50)
			});

			// Add layer width station markers
			this.map.addLayer({
				id: 'unclustered-station',
				type: 'circle',
				source: this.$options.STATIONS_SOURCE_ID,
				filter: ['==', 'is_active', true],
				paint: {
					'circle-color': '#11b4da',
					'circle-radius': 0,
					'circle-stroke-width': 1,
					'circle-stroke-color': '#fff'
				}
			});

			// Add layers with cluster markers
			this.map.addLayer({
				id: 'station-clusters',
				type: 'circle',
				source: this.$options.STATIONS_SOURCE_ID,
				filter: ['has', 'point_count'],
				paint: {
					// Use step expressions (https://docs.maptiler.com/gl-style-specification/expressions/#step)
					// with three steps to implement three types of circles:
					//   * Blue, 20px circles when point count is less than 100
					//   * Yellow, 30px circles when point count is between 100 and 750
					//   * Pink, 40px circles when point count is greater than or equal to 750
					'circle-color': [
						'step',
						['get', 'point_count'],
						'#5182c9',
						100,
						'#f1f075',
						750,
						'#f28cb1'
					],
					'circle-radius': [
						'step',
						['get', 'point_count'],
						20,
						100,
						30,
						750,
						40
					]
			}
			});
			this.map.addLayer({
				id: 'station-cluster-count',
				type: 'symbol',
				source: this.$options.STATIONS_SOURCE_ID,
				filter: ['has', 'point_count'],
				layout: {
					'text-field': '{point_count_abbreviated}',
					'text-font': ['Roboto', 'Arial Unicode MS Bold'],
					'text-size': 12
				}
			});
		},

		renderStationMarkers() {
            const features = this.map.querySourceFeatures(this.$options.STATIONS_SOURCE_ID);
			
			// Hide all markers that shown
			for (const id of Object.keys(this.mapMarkersOnScreen)) {
				this.setStationMarkerVisibility(id, false)
			}
			this.mapMarkersOnScreen = {}
            // for every station on the screen, bind an HTML marker for it,
            // and add it to the map if it's not there already
            features.forEach((feature) => {
				//console.log("curent feature = ", feature)

                //const coords = features[i].geometry.coordinates;
                const props = feature.properties;
				// If feature is cluster
				if (props.cluster) {
					//
				} else {
					//console.log("curent station name = ", props.name)
					const id = props.id
					
					var marker = this.mapMarkers[id]
					//console.log("curent station marker = ", marker)
					if (!marker) {
						this.addStationMarker(this.stationsStore.station(id))
						marker = this.mapMarkers[id]
						//console.log("Create station marker = ", marker)
					}
					
					// Update coordinates for correct position if multiple create and remove station on one session
					marker.setLngLat(this.stationsStore.station(id).passport.get(Station.passportKeys.location).coordinates)
					this.mapMarkersOnScreen[id] = marker

					this.setStationMarkerVisibility(id, true)
				}
            })
		},

		addStationMarker(station) {
			//console.log("marker DOM el: ", this.$refs[station.id][0])

			// Bind marker to map coordinates
			// Add popup to marker
			// Save to array
			this.mapMarkers[station.id] = new Marker({element: this.$refs[station.id][0],})
			.setLngLat(station.passport.get(Station.passportKeys.location).coordinates)
			.addTo(this.map)
		},

		addPopupToStationMarker(marker, station) {
			const markerRadius = 10, linearOffset = 25;
			const popupOffsets = {
				'top': [0, 0],
				'top-left': [0,0],
				'top-right': [0,0],
				'bottom': [0, -this.marckHeight/2],
				'bottom-left': [linearOffset, (this.marckHeight/2 - markerRadius + linearOffset) * -1],
				'bottom-right': [-linearOffset, (this.marckHeight/2 - markerRadius + linearOffset) * -1],
				'left': [markerRadius, (this.marckHeight/2 - markerRadius) * -1],
				'right': [-markerRadius, (this.marckHeight/2 - markerRadius) * -1]
			};

			marker.setPopup(
				new Popup({
					offset: popupOffsets,
					maxWidth: "400px",
					closeButton: false,
				}).setHTML(this.stationPopupHtml(station))
			)
		},

		stationPopupHtml(station) {
			const name = `<span>${station.name}</span>`
			let warnings = []
			let content = ""

			// If protection potential is not normal
			if (station.protectionRange != "ok") {
				const warningStyle = `text-align: center; display: block; margin: 0 auto; color: Tomato;`
				warnings.push(`<strong style="${warningStyle}">Захисний потенціал = ${station.eOut?.toFixed(2) ?? 'невідомо'}</strong>`)
			}
			
			// If no connection
			if (station.power_unit.isConnect === false ||
				station.service_unit.isConnect === false) {
				const warningStyle = `text-align: center; display: block; margin: 0 auto; color: Silver;`
				let warning = `<strong style="${warningStyle}">`
				if (station.power_unit.isConnect === false &&
					station.service_unit.isConnect === false) {
					warning += `Немає зв'язку`
				} else if (station.power_unit.isConnect === false) {
					warning += `Немає зв'язку з силовим модулем`
				} else if (station.service_unit.isConnect === false) {
					warning += `Немає зв'язку з севісним модулем`
				}
				warning += `</strong>`

				warnings.push(warning)
			}

			// Assemble popup html
			content = `${name}`
			warnings.forEach(w => {
				content += `${w}`
			})

			return content
		},

		setStationMarkerVisibility(stationId, isVisible) {
			if (this.$refs[stationId][0] !== undefined) {
				this.$refs[stationId][0].style.visibility = isVisible ? "visible" : "hidden";
			}
		},

		stationMarkerColor(station) {
			// station
			// const markerColors = ["Silver", "LightGreen", "Gold", "Tomato"]
			// return markerColors[Math.floor(Math.random() * 3)]

			let color = "LightGreen"

			// If station have alerts
			if (station.alrts?.length > 0) {
				color = "Gold"
			}

			// If protection potential is not normal
			if (station.protectionRange != "ok") {
				//console.log(`Name: ${station.name}. Potential out of range!`)
				color = "Tomato"
			}

			// If no connection to any unit
			if (station.power_unit.isConnect === false ||
				station.service_unit.isConnect === false) {
				color = "Silver"
				//console.log(`Name: ${station.name}, Connection: ${station.power_unit.isConnect}, ${station.service_unit.isConnect}`)
			}
			
			return color
		},

		
		updateStationsGeoJson() {
			this.stationsGeoJson.features.length = 0
			this.stations.forEach(station => {
				this.stationsGeoJson.features.push({
					type: "Feature",
					properties: {
						id: station.id,
						name: station.name,
						is_active: station.isActive,
						markerColor: this.stationMarkerColor(station),
					},
					geometry: {
						type: "Point",
						coordinates: station.passport.get(Station.passportKeys.location).coordinates,
					},
				})
			})

			const mapSource = this.map?.getSource(this.$options.STATIONS_SOURCE_ID)
			if (mapSource !== undefined) {
				//console.log("Reset GeoJson!")
				mapSource.setData(this.stationsGeoJson);
			}
		},

		updateStationMarksColor() {
			this.stationMarkersColor = {}

			this.stations.forEach(station => {
				this.stationMarkersColor[station.id] = this.stationMarkerColor(station)
			})
		},

		showStationMarkerContextMenu(event, station) {
			//console.log("map cpntainer ref", this.$refs.mapContainer.getBoundingClientRect())
			//console.log("event ", event, station.name)
			event.preventDefault();

			this.hideStationContextMenu()
			
			const rect = this.mapContainerEl.getBoundingClientRect()
			this.stationContextMenuVisible = true;
			//console.log(`event.pageY=${event.pageY}, getBoundingClientRect().y=`, rect)
			this.stationContextMenuPosition = {
				top: event.pageY - rect.y,
				left: event.pageX - rect.x
			};
			this.stationForContextMenu = station
		},

		hideStationContextMenu() {
			this.stationContextMenuVisible = false;
		},

		removeStationLocation(station) {
			this.hideStationContextMenu ()

			//station.passport.set(Station.passportKeys.location, null)
			station.setLocation(0, 0)
			
			let payload = {}
			payload[Station.passportKeys.location] = station.passport.get(Station.passportKeys.location)
			//console.log("Location payload", payload)

			this.stationsStore.updatePasport(async (result) => {
				if (result) {
					if (this.mapMarkers[station.id] !== undefined) {
						//await this.updateStations()
						this.setStationMarkerVisibility(station.id, false)
					}
				} else {
					console.error("Passport update error")
				}
			}, station.id, payload)
		},

		onClickStationMarker(station) {
			//console.log("Click on station marker event occured")
			this.addPopupToStationMarker(this.mapMarkers[station.id], station)
		},
	},

	computed: {
		marckHeight() { return 63 },
		marckWidth() { return 31 },
	}
}
</script>


<style scoped>
.station-marker {
	cursor: pointer;
	z-index: 1;
	visibility: hidden;
}
</style>